<template>
  <!--
    "Limit Use of Sensitive Personal Information" landing page. CCPA
    requires this link in the footer alongside Do-Not-Sell. The page
    explains Kiey's stance and offers a one-click affirmative limit
    via the same /v1/legal/do_not_sell endpoint with scope=
    'limit_sensitive_pi'.
  -->
  <div class="legal-page">
    <div class="legal-page-card">
      <div class="legal-page-header">
        <h1 class="legal-page-title">Limit Use of Sensitive Personal Information</h1>
      </div>

      <div class="ls-body">
        <p>
          Kiey does not use sensitive personal information to infer
          characteristics about you. Your sensitive personal information
          is processed only to provide the Kiey Service, perform the
          contract you have with us, and meet our legal obligations.
        </p>
        <p>
          To affirmatively further limit Kiey's use of your sensitive
          personal information beyond these necessary purposes, click
          the button below.
        </p>

        <div v-if="submitted" class="ls-success" role="status" aria-live="polite">
          <h2>Limit recorded</h2>
          <p>Tracking ID: <span class="tracking-id">{{ tracking_id }}</span></p>
          <p>Thanks. We've recorded your request.</p>
        </div>

        <form v-else class="ls-form" @submit.prevent="onSubmit">
          <label class="ls-label">
            Email address <span class="ls-required">*</span>
            <input v-model.trim="email" type="email" required class="ls-input" :disabled="submitting" />
          </label>
          <p v-if="error" class="ls-error" role="alert">{{ error }}</p>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? "Submitting…" : "Confirm limit request" }}
          </button>
        </form>

        <hr />
        <p class="ls-fine-print">
          You can also email <a href="mailto:support@kiey.com">support@kiey.com</a> or use our
          <a href="/legal/privacy-request">privacy request form</a>.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import config from "@/config/constants.js";

export default {
  name: "LimitSensitivePI",
  data() {
    return {
      email: "",
      submitting: false,
      submitted: false,
      tracking_id: "",
      error: "",
    };
  },
  computed: {
    current_user() {
      return (this.$store && this.$store.state && this.$store.state.Authenticate && this.$store.state.Authenticate.user) || null;
    },
  },
  mounted() {
    if (this.current_user && this.current_user.email) {
      this.email = this.current_user.email;
    }
  },
  methods: {
    async onSubmit() {
      this.error = "";
      if (!this.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) {
        this.error = "Please enter a valid email address.";
        return;
      }
      this.submitting = true;
      try {
        const headers = {};
        try {
          const token = localStorage.getItem(config.token_key || "web-auth");
          if (token) headers.Authorization = token;
        } catch (e) { /* localStorage unavailable */ }
        const res = await axios.post(
          config.api_url + "legal/do_not_sell",
          {
            email: this.email,
            scope: "limit_sensitive_pi",
          },
          { headers }
        );
        const data = (res && res.data && res.data.data) || {};
        if (data.tracking_id) {
          this.tracking_id = data.tracking_id;
          this.submitted = true;
        } else {
          this.error = "Unexpected response. Please try again.";
        }
      } catch (e) {
        const status = e && e.response && e.response.status;
        if (status === 429) {
          this.error = "Too many submissions from this address. Please try again later.";
        } else {
          this.error = "Could not submit your request. Please email support@kiey.com.";
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background: #f5f7fb;
  padding: 32px 16px;
  box-sizing: border-box;
}
.legal-page-card {
  max-width: 720px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(6, 29, 71, 0.06);
  padding: 36px 40px;
}
.legal-page-header {
  border-bottom: 1px solid #e4e8f1;
  padding-bottom: 14px;
  margin-bottom: 22px;
}
.legal-page-title {
  color: #061d47;
  font-size: 24px;
  margin: 0;
}
.ls-body p {
  font-size: 14px;
  color: #2d2d2d;
  line-height: 1.6;
  margin: 0 0 14px;
}
.ls-form { margin: 14px 0; }
.ls-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #4d566c;
  margin-bottom: 12px;
}
.ls-required { color: #c0392b; }
.ls-input {
  display: block;
  width: 100%;
  margin-top: 6px;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid #d3d8e3;
  border-radius: 6px;
  box-sizing: border-box;
}
.ls-input:focus {
  outline: none;
  border-color: #29488d;
  box-shadow: 0 0 0 3px rgba(41, 73, 141, 0.12);
}
.ls-error {
  background: #fbecec;
  color: #b13c3c;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
}
.btn-primary {
  display: inline-block;
  background: #29488d;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  border: 0;
  border-radius: 6px;
  padding: 12px 22px;
  cursor: pointer;
}
.btn-primary:hover { background: #1f3771; }
.btn-primary:disabled { background: #8b97b7; cursor: not-allowed; }
.ls-success {
  background: #e6f3ec;
  border-radius: 8px;
  padding: 18px 22px;
  margin: 14px 0 22px;
}
.ls-success h2 { color: #1f8a4a; font-size: 18px; margin: 0 0 8px; }
.ls-success p { margin: 0 0 6px; }
.tracking-id {
  display: inline-block;
  background: #fff;
  color: #061d47;
  font-family: "SF Mono", Menlo, Monaco, Consolas, monospace;
  font-weight: 700;
  font-size: 13px;
  padding: 4px 10px;
  border-radius: 4px;
}
.ls-fine-print { font-size: 12px; color: #6a7388; margin: 18px 0 0; }
.ls-fine-print a { color: #29488d; }
hr { border: 0; border-top: 1px solid #e4e8f1; margin: 24px 0 18px; }
@media (max-width: 720px) {
  .legal-page-card { padding: 24px 18px; }
  .legal-page-title { font-size: 20px; }
}
</style>
